import initSqlJs, { type Database } from 'sql.js'

export interface Airport {
  icaoCode: string
  name: string | null
  municipality: string | null
  timezone: string
  latitudeDeg: number
  longitudeDeg: number
  countryName: string | null
  regionName: string | null
  iataCode: string | null
  type: string | null
  isoCountry: string | null
}

let db: Database | null = null
let initPromise: Promise<void> | null = null

async function initDb(): Promise<void> {
  const SQL = await initSqlJs({
    locateFile: () => '/sql-wasm.wasm',
  })

  const response = await fetch('/airports.db')
  const buffer = await response.arrayBuffer()
  db = new SQL.Database(new Uint8Array(buffer))
}

export async function ensureAirportDb(): Promise<void> {
  if (db) return
  if (!initPromise) {
    initPromise = initDb().catch((err) => {
      initPromise = null
      throw err
    })
  }
  await initPromise
}

function rowToAirport(row: any[]): Airport {
  return {
    icaoCode: row[0] as string,
    name: row[1] as string | null,
    municipality: row[2] as string | null,
    timezone: row[3] as string,
    latitudeDeg: row[4] as number,
    longitudeDeg: row[5] as number,
    countryName: row[6] as string | null,
    regionName: row[7] as string | null,
    iataCode: row[8] as string | null,
    type: row[9] as string | null,
    isoCountry: row[10] as string | null,
  }
}

const ALL_COLUMNS = 'icaoCode, name, municipality, timezone, latitudeDeg, longitudeDeg, countryName, regionName, iataCode, type, isoCountry'

export function getAirportByIcao(icao: string): Airport | null {
  if (!db) return null
  const stmt = db.prepare(`SELECT ${ALL_COLUMNS} FROM airports WHERE icaoCode = ?`)
  stmt.bind([icao])
  if (stmt.step()) {
    const airport = rowToAirport(stmt.get())
    stmt.free()
    return airport
  }
  stmt.free()
  return null
}

export function getAirportsByIcaoCodes(codes: string[]): Airport[] {
  if (!db || codes.length === 0) return []
  const placeholders = codes.map(() => '?').join(',')
  const results = db.exec(`SELECT ${ALL_COLUMNS} FROM airports WHERE icaoCode IN (${placeholders})`, codes)
  if (results.length === 0) return []
  return results[0].values.map(rowToAirport)
}

export function listAirports(offset: number, limit: number): Airport[] {
  if (!db) return []
  const results = db.exec(
    `SELECT ${ALL_COLUMNS} FROM airports
     ORDER BY icaoCode ASC
     LIMIT ? OFFSET ?`,
    [limit, offset]
  )
  if (results.length === 0) return []
  return results[0].values.map(rowToAirport)
}

// Directory search: 1–4 chars match ICAO codes by prefix only (ICAO codes are
// 4 chars, so a region letter like "K" browses a whole country); 5+ chars
// search the full airport name instead. The picker keeps the broader
// `searchAirports` behavior.
export function searchAirportsDirectory(query: string, limit = 200): Airport[] {
  if (!db || !query) return []
  if (query.length <= 4) {
    const results = db.exec(
      `SELECT ${ALL_COLUMNS} FROM airports
       WHERE icaoCode LIKE ? COLLATE NOCASE
       ORDER BY icaoCode ASC
       LIMIT ?`,
      [`${query}%`, limit]
    )
    if (results.length === 0) return []
    return results[0].values.map(rowToAirport)
  }
  const results = db.exec(
    `SELECT ${ALL_COLUMNS} FROM airports
     WHERE name LIKE ? COLLATE NOCASE
     ORDER BY icaoCode ASC
     LIMIT ?`,
    [`%${query}%`, limit]
  )
  if (results.length === 0) return []
  return results[0].values.map(rowToAirport)
}

export function searchAirports(query: string, limit = 50): Airport[] {
  if (!db || !query || query.length < 2) return []
  const q = `%${query}%`
  const results = db.exec(
    `SELECT ${ALL_COLUMNS} FROM airports
     WHERE icaoCode LIKE ? COLLATE NOCASE
        OR name LIKE ? COLLATE NOCASE
        OR municipality LIKE ? COLLATE NOCASE
        OR countryName LIKE ? COLLATE NOCASE
        OR regionName LIKE ? COLLATE NOCASE
        OR iataCode LIKE ? COLLATE NOCASE
     ORDER BY
       CASE WHEN icaoCode LIKE ? COLLATE NOCASE THEN 0 ELSE 1 END,
       name ASC
     LIMIT ?`,
    [q, q, q, q, q, q, q, limit]
  )
  if (results.length === 0) return []
  return results[0].values.map(rowToAirport)
}
